import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { PANEL_IDS } from '../config/panelRegistry';
import { usePanelSettings } from '../contexts/PanelSettingsContext';
import { createCallNumberFilter, deleteCallNumberFilter, fetchCallNumberFilters } from '../api';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from './ui/alert-dialog';
import InfoHint from './InfoHint';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const defaultFormState = { pattern: '', comment: '' };

const describeError = (error) =>
  error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.';

const NumberFilters = () => {
  const panels = usePanelSettings();
  const { toast } = useToast();
  const [filters, setFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [filterToDelete, setFilterToDelete] = useState(null);

  const loadFilters = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchCallNumberFilters();
      setFilters(Array.isArray(response) ? response : []);
    } catch (error) {
      toast({
        title: 'Не удалось загрузить фильтры',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const closeDialog = () => {
    setDialogOpen(false);
    setFormState(defaultFormState);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formState.pattern.trim()) {
      toast({
        title: 'Введите номер',
        description: 'Укажите номер целиком или маску вида 7495*.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createCallNumberFilter({
        pattern: formState.pattern.trim(),
        comment: formState.comment.trim() || null
      });

      toast({
        title: 'Номер добавлен',
        description: 'Новые звонки с этого номера обрабатываться не будут.'
      });

      closeDialog();
      await loadFilters();
    } catch (error) {
      // Сообщения о некорректной маске и о дубликате приходят с сервера уже по-русски.
      toast({
        title: 'Не удалось добавить номер',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!filterToDelete?.id) {
      return;
    }

    try {
      await deleteCallNumberFilter(filterToDelete.id);
      toast({
        title: 'Фильтр удалён',
        description: `Звонки с номера ${filterToDelete.pattern} снова обрабатываются.`
      });
      setFilterToDelete(null);
      await loadFilters();
    } catch (error) {
      toast({
        title: 'Не удалось удалить фильтр',
        description: describeError(error),
        variant: 'destructive'
      });
    }
  };

  return (
    <Card data-panel-id={PANEL_IDS['ui:number-filters.list']}>
      <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-1.5">
          <CardTitle>{panels.title(PANEL_IDS['ui:number-filters.list'])}</CardTitle>
          <InfoHint
            text={panels.description(PANEL_IDS['ui:number-filters.list'])}
            label="О разделе"
            trackId="number_filters.header.hint"
          />
        </div>
        <Button data-ev="number_filters.add" onClick={() => setDialogOpen(true)} className="gap-2 md:self-start md:shrink-0">
          <Plus className="h-4 w-4" />
          Добавить номер
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Номер</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead>Комментарий</TableHead>
                <TableHead>Добавлен</TableHead>
                <TableHead className="w-[140px] text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    Загружаем фильтры...
                  </TableCell>
                </TableRow>
              ) : filters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    Фильтры не добавлены — обрабатываются все звонки.
                  </TableCell>
                </TableRow>
              ) : (
                filters.map((filter) => (
                  <TableRow key={filter.id}>
                    <TableCell>
                      <div className="font-medium">{filter.pattern}</div>
                      {filter.normalizedPattern !== filter.pattern && (
                        <div className="text-xs font-mono text-muted-foreground">
                          {filter.normalizedPattern}
                          {filter.isPrefix ? '…' : ''}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={filter.isPrefix ? 'secondary' : 'outline'}>
                        {filter.isPrefix ? 'Префикс' : 'Точное совпадение'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {filter.comment || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {new Date(filter.createdAtUtc).toLocaleString('ru-RU')}
                      </div>
                      {filter.createdByUsername && (
                        <div className="text-xs text-muted-foreground">{filter.createdByUsername}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          variant="destructive"
                          size="sm"
                          data-ev="number_filters.delete"
                          onClick={() => setFilterToDelete(filter)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Удалить
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent trackId="number_filters.add_dialog">
          <DialogHeader>
            <DialogTitle>Новый фильтр</DialogTitle>
            <DialogDescription>
              Укажите номер целиком или маску: звёздочка в конце исключает все номера,
              начинающиеся с указанных цифр.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="number-filter-pattern">Номер или маска</Label>
              <Input
                id="number-filter-pattern"
                data-ev="number_filters.pattern"
                value={formState.pattern}
                onChange={(event) =>
                  setFormState((previous) => ({ ...previous, pattern: event.target.value }))
                }
                placeholder="+7 495 123-45-67 или 7495*"
              />
              <p className="text-xs text-muted-foreground">
                Сравниваются оба номера звонка и добавочные, независимо от направления.
                Звёздочка допустима только в конце.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="number-filter-comment">Комментарий (необязательно)</Label>
              <Input
                id="number-filter-comment"
                data-ev="number_filters.comment"
                value={formState.comment}
                onChange={(event) =>
                  setFormState((previous) => ({ ...previous, comment: event.target.value }))
                }
                placeholder="Например, робозвонщик"
              />
            </div>

            {/* У входящих звонков номер вашей линии приезжает как вторая сторона, поэтому
                фильтр по нему выключил бы обработку всего входящего потока. */}
            <div className="flex items-start gap-3 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Не добавляйте сюда номер своей линии — под фильтр попадут все входящие
                звонки на неё.
              </span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" data-ev="number_filters.add_cancel" onClick={closeDialog}>
                Отмена
              </Button>
              <Button type="submit" data-ev="number_filters.add_submit" disabled={isSubmitting}>
                {isSubmitting ? 'Сохраняем...' : 'Добавить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(filterToDelete)}
        onOpenChange={(open) => !open && setFilterToDelete(null)}
      >
        <AlertDialogContent trackId="number_filters.delete_dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Удалить фильтр?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Новые звонки с номера {filterToDelete?.pattern ?? ''} снова будут обрабатываться.
              Уже пропущенные задания останутся пропущенными — их можно запустить кнопкой
              «Переобработать» на вкладке «Задания».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-ev="number_filters.delete_cancel">Отмена</AlertDialogCancel>
            <AlertDialogAction data-ev="number_filters.delete_confirm" className="bg-destructive hover:bg-destructive/90" onClick={handleDelete}>
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default NumberFilters;
